import { createLogger } from '@/debug/logger'
import {
  getDocumentThumbnailMeta,
  setDocumentThumbnailMeta,
  type DocumentMeta,
} from '@/persistence/localStore'
import { getCachedThumbnailImage, loadThumbnailImage, setThumbnailImage } from '@/persistence/thumbnailStore'
import { loadDocumentSnapshot, readDocument } from '@/persistence/local'
import type { Snapshot } from '@/document/model'
import {
  DEFAULT_THUMBNAIL_HEIGHT,
  DEFAULT_THUMBNAIL_WIDTH,
  THUMBNAIL_RENDER_VERSION,
  thumbnailMetaCoversSize,
  thumbnailRenderDimensions,
} from '@/capture/thumbnailRenderConfig'

export type EnsureThumbnailOptions = { width?: number; height?: number; commitId?: number }

type PipelineJob = {
  id: string
  gen: number
  width: number
  height: number
  commitId: number
  resolve: (url: string) => void
  reject: (err?: unknown) => void
}

type ThumbnailSource = {
  snapshot: Snapshot
  commitId: number
}

const pending = new Map<string, { promise: Promise<string>; gen: number; commitId: number; width: number; height: number }>()
const generations = new Map<string, number>()
const queue: PipelineJob[] = []
let running = false

const log = createLogger('thumbnails')

export function getCachedThumbnail(id: string): string | null {
  return getCachedThumbnailImage(id)
}

export function loadCachedThumbnail(id: string): Promise<string | null> {
  return loadThumbnailImage(id)
}

export function getCachedThumbnailMeta(id: string) {
  return getDocumentThumbnailMeta(id)
}

export function isThumbnailStale(meta: DocumentMeta): boolean {
  return isThumbnailStaleForSize(meta)
}

export function isThumbnailStaleForSize(meta: DocumentMeta, opts?: EnsureThumbnailOptions): boolean {
  const cachedMeta = getDocumentThumbnailMeta(meta.id)
  if (!cachedMeta) return true
  const target = thumbnailRenderDimensions(opts?.width ?? DEFAULT_THUMBNAIL_WIDTH, opts?.height ?? DEFAULT_THUMBNAIL_HEIGHT)
  return meta.commitId !== cachedMeta.commitId
    || cachedMeta.renderVersion !== THUMBNAIL_RENDER_VERSION
    || !thumbnailMetaCoversSize(cachedMeta, target.width, target.height)
}

async function readCachedThumbnail(id: string, commitId: number, width: number, height: number): Promise<string | null> {
  let cached: string | null = null
  try {
    cached = await loadThumbnailImage(id)
  } catch (err) {
    log.warn('failed to read thumbnail cache', { id, err })
  }
  const cachedMeta = getDocumentThumbnailMeta(id)
  if (
    cached
    && cachedMeta
    && cachedMeta.commitId === commitId
    && cachedMeta.renderVersion === THUMBNAIL_RENDER_VERSION
    && thumbnailMetaCoversSize(cachedMeta, width, height)
  ) {
    log.debug('cache hit', { id, commitId, renderVersion: cachedMeta.renderVersion, width, height })
    return cached
  }
  return null
}

async function loadThumbnailSource(id: string): Promise<ThumbnailSource> {
  const snapshot = await loadDocumentSnapshot(id)
  const commitId = snapshot.commitId ?? (readDocument(id)?.commitId ?? 0)
  if (log.debugEnabled()) {
    const sample: Array<{ key: number; type: string | undefined }> = []
    snapshot.base.forEach((voxel, key) => {
      if (sample.length < 5) {
        sample.push({ key, type: voxel?.type })
      }
    })
    log.debug('snapshot sample', { id, commitId, sample })
  }
  log.debug('loaded snapshot', {
    id,
    commitId,
    baseSize: snapshot.base.size,
  })
  return { snapshot, commitId }
}

async function persistThumbnail(id: string, url: string, commitId: number, width: number, height: number): Promise<string> {
  const existingMeta = getDocumentThumbnailMeta(id)
  if (existingMeta && existingMeta.commitId > commitId) {
    log.debug('skip persisting stale thumbnail', { id, commitId, existingCommit: existingMeta.commitId })
    return url
  }
  try {
    const persistedUrl = await setThumbnailImage(id, url)
    setDocumentThumbnailMeta(id, { commitId, renderVersion: THUMBNAIL_RENDER_VERSION, width, height })
    log.debug('persisted thumbnail', { id, commitId, renderVersion: THUMBNAIL_RENDER_VERSION, width, height })
    return persistedUrl
  } catch (err) {
    log.warn('failed to persist thumbnail cache', { id, commitId, err })
    return url
  }
}

async function processJob(job: PipelineJob) {
  log.debug('run job', {
    id: job.id,
    gen: job.gen,
    width: job.width,
    height: job.height,
    commitId: job.commitId,
  })

  const source = await loadThumbnailSource(job.id)
  if (source.commitId < job.commitId) {
    log.debug('loaded snapshot older than requested commit', {
      id: job.id,
      requested: job.commitId,
      snapshotCommit: source.commitId,
    })
  }

  const cached = await readCachedThumbnail(job.id, source.commitId, job.width, job.height)
  if (cached && source.commitId >= job.commitId) {
    job.resolve(cached)
    return
  }

  try {
    // The capture renderer (a WebGPU canvas and the voxel renderers) is its own chunk: home shows saved
    // thumbnails without it and loads it only when one has to be drawn.
    const { enqueueThumbnailJob } = await import('@/capture/queue')
    const url = await enqueueThumbnailJob({id: job.id, snapshot: source.snapshot, opts: {width: job.width, height: job.height}})
    if (url) {
      const currentGen = generations.get(job.id)
      const isLatest = currentGen == null || job.gen === currentGen
      let resolvedUrl = url
      if (isLatest) {
        resolvedUrl = await persistThumbnail(job.id, url, source.commitId, job.width, job.height)
      } else {
        log.debug('discard stale render', { id: job.id, gen: job.gen, currentGen })
      }
      job.resolve(resolvedUrl)
      return
    }
    job.resolve('')
  } catch (err) {
    log.error('job failed', { id: job.id, gen: job.gen, err })
    job.reject(err)
  }
}

async function runNext() {
  if (running) return
  running = true
  try {
    while (queue.length > 0) {
      const job = queue.shift()!
      try {
        await processJob(job)
      } finally {
        const pendingEntry = pending.get(job.id)
        if (pendingEntry && pendingEntry.gen === job.gen) {
          pending.delete(job.id)
        }
        await new Promise((resolve) => {
          setTimeout(resolve, 50)
        })
      }
    }
  } finally {
    running = false
  }
}

export async function ensureThumbnail(id: string, opts?: EnsureThumbnailOptions): Promise<string> {
  if (!id) return Promise.resolve('')

  const requestedWidth = Math.max(1, Math.floor(opts?.width ?? DEFAULT_THUMBNAIL_WIDTH))
  const requestedHeight = Math.max(1, Math.floor(opts?.height ?? DEFAULT_THUMBNAIL_HEIGHT))
  const { width, height } = thumbnailRenderDimensions(requestedWidth, requestedHeight)
  const targetCommitId =
    typeof opts?.commitId === 'number' && opts.commitId >= 0
      ? opts.commitId
      : readDocument(id)?.commitId ?? 0

  log.debug('ensure request', { id, requestedWidth, requestedHeight, width, height, commitId: targetCommitId })
  const cached = await readCachedThumbnail(id, targetCommitId, width, height)
  if (cached) return Promise.resolve(cached)

  const existing = pending.get(id)
  if (existing && existing.commitId >= targetCommitId && existing.width >= width && existing.height >= height) {
    log.debug('coalesce pending job', { id, commitId: existing.commitId, width: existing.width, height: existing.height })
    return existing.promise
  }

  const nextGen = (generations.get(id) || 0) + 1
  generations.set(id, nextGen)

  const promise = new Promise<string>((resolve, reject) => {
    queue.push({ id, gen: nextGen, width, height, commitId: targetCommitId, resolve, reject })
    runNext().catch((err) => {
      log.error('runner failure', { err })
    })
  })

  pending.set(id, { promise, gen: nextGen, commitId: targetCommitId, width, height })
  log.debug('queued job', { id, gen: nextGen, commitId: targetCommitId, width, height })

  return promise
}
